import type { DecipherDatamap } from '../report-aggregation';
import { DataExport, isNumeric } from './data-export';

/**
 * Decipher's datamap as its own exports write it, shared by the CSV download's `datamap.txt` and the
 * workbook's Datamap sheet -- the same blocks, differently laid out, as the upload converters'
 * EndClientReportDecipherDatamap reads them back.
 */

type DatamapVariable = DecipherDatamap['variables'][number];

/** One question as Decipher's datamap lists it: a header, how it is answered, and the columns it fills. */
export interface DatamapBlock {
  label: string;
  title: string;
  /** Headed `[Q2]:`, the question's one column. Otherwise `Q7:`, with its columns listed beneath. */
  singleColumn: boolean;
  answers: 'coded' | 'text' | 'numeric';
  values: { code: number; title: string }[];
  columns: { label: string; title: string }[];
}

/** Columns A, B and C of the workbook's Datamap sheet. `datamap.txt` is the same rows as text. */
export type DatamapRow = [string, string | number, string];

const OPEN_TEXT = 'Open text response';
const OPEN_NUMERIC = 'Open numeric response';
const WEIGHT_TITLE = 'Respondent weight';

/** Decipher's own wording for a multi-punch column, which the API datamap leaves out. */
const PUNCH_VALUES = [{ code: 0, title: 'Unchecked' }, { code: 1, title: 'Checked' }];

/**
 * Decipher's quota and termination counters (`vterm`, `vqtable3r0`, `voqtable3r0`...). Its CSV
 * download carries them and its Excel and SPSS downloads don't. Prefixes because they're numbered
 * per quota; the upload converter knows them by the same list (EndClientReportDecipherDatamap).
 */
const COUNTER_PREFIXES = ['vterm', 'vqtable', 'voqtable'];

export function isDecipherCounter(column: string): boolean {
  const name = column.toLowerCase();
  return COUNTER_PREFIXES.some(prefix => name.startsWith(prefix));
}

/** The download as Decipher's Excel and SPSS exports have it: without its quota counters. */
export function withoutCounters(data: DataExport): DataExport {
  return {
    ...data,
    columns: data.columns.filter(column => !isDecipherCounter(column)),
    datamap: { ...data.datamap, variables: (data.datamap.variables ?? []).filter(variable => !isDecipherCounter(variable.label)) },
  };
}

/** The download's blocks: the source's datamap, then the weight where the report is weighted. */
export function decipherBlocks(data: DataExport): DatamapBlock[] {
  const blocks = datamapBlocks(data.datamap);
  if (!data.weight) return blocks;
  return [...blocks, {
    label: data.weight.column, title: WEIGHT_TITLE, singleColumn: true, answers: 'numeric', values: [], columns: [],
  }];
}

/**
 * One block per `vgroup`, where its first column is. A question's columns needn't sit together in
 * the data -- Decipher's quota tables are split across it -- and Decipher still lists each once.
 */
export function datamapBlocks(datamap: DecipherDatamap): DatamapBlock[] {
  const groups = new Map<string, DatamapVariable[]>();
  for (const variable of datamap.variables ?? []) {
    const group = variable.vgroup || variable.label;
    const members = groups.get(group);
    if (members) members.push(variable);
    else groups.set(group, [variable]);
  }
  return [...groups.values()].map(blockOf);
}

function blockOf(variables: DatamapVariable[]): DatamapBlock {
  const first = variables[0];
  const group = first.vgroup || first.label;
  // A question with one column is listed as that column, as Decipher lists it, whatever its type.
  const singleColumn = variables.length === 1;
  const label = singleColumn ? first.label : group;
  // A column that is its own question has the fuller title ("Q30 - Other, please specify"); the one
  // column of a bigger question takes the question's.
  const title = (singleColumn && first.label === group ? first.title : first.qtitle) ?? first.title ?? label;

  let answers: DatamapBlock['answers'] = 'text';
  let values: DatamapBlock['values'] = [];
  if (first.type === 'multiple') {
    answers = 'coded';
    values = PUNCH_VALUES;
  } else if (first.values?.length) {
    answers = 'coded';
    values = first.values.map(value => ({ code: value.value, title: plainText(value.title ?? '') }));
  } else if (isNumeric(first)) {
    answers = 'numeric';
  }

  return {
    label,
    title: plainText(title),
    singleColumn,
    answers,
    values,
    columns: singleColumn ? [] : variables.map(variable => ({ label: variable.label, title: plainText(columnTitle(variable)) })),
  };
}

function columnTitle(variable: DatamapVariable): string {
  if (variable.rowTitle && variable.colTitle) return `${variable.rowTitle} - ${variable.colTitle}`;
  return variable.colTitle || variable.rowTitle || variable.title || variable.label;
}

/**
 * Survey wording arrives as HTML. Decipher's text exports keep what the HTML parser reads -- tags
 * dropped, `&nbsp;` as a real non-breaking space, trailing spaces kept -- so this does the same. A
 * line break would start a new datamap line, so those become spaces.
 */
export function plainText(html: string): string {
  const text = /[<&]/.test(html) ? new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '' : html;
  return text.replace(/[\r\n\t]+/g, ' ');
}

export function datamapRows(blocks: DatamapBlock[]): DatamapRow[] {
  return blocks.flatMap(block => [...blockRows(block), ['', '', ''] as DatamapRow]);
}

function blockRows(block: DatamapBlock): DatamapRow[] {
  const header = block.singleColumn ? `[${block.label}]: ${block.title}` : `${block.label}: ${block.title}`;
  const rows: DatamapRow[] = [[header, '', '']];
  if (block.answers === 'coded') {
    const codes = block.values.map(value => value.code);
    rows.push([`Values: ${Math.min(...codes)}-${Math.max(...codes)}`, '', '']);
    rows.push(...block.values.map(value => ['', value.code, value.title] as DatamapRow));
  } else {
    // The API datamap keeps no allowed range, which is what Decipher's "Values: 1900-2025" line
    // would need; an upload reads this line the same way.
    rows.push([block.answers === 'numeric' ? OPEN_NUMERIC : OPEN_TEXT, '', '']);
  }
  rows.push(...block.columns.map(column => ['', `[${column.label}]`, column.title] as DatamapRow));
  return rows;
}

/** Decipher's `datamap.txt`: options as ` 1=Title`, columns as ` [Q7r1] Title`, blocks a blank line apart. */
export function datamapText(blocks: DatamapBlock[]): string {
  const lines = datamapRows(blocks).map(([line, code, title]) => {
    if (line || code === '') return line;
    return typeof code === 'number' ? ` ${code}=${title}` : ` ${code} ${title}`;
  });
  return lines.join('\n') + '\n';
}
